import React, { Component } from "react";
import { ENABLE_STRIPE, ENABLE_RHI } from "../../config/featureFlags";
import { Game } from "../../types/game";
import withStyles from "@material-ui/core/styles/withStyles";
import withWidth, { isWidthDown, WithWidth } from "@material-ui/core/withWidth";
import {
  Theme,
  Checkbox,
  WithStyles,
  Typography,
  Button,
  createStyles,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  InputBase,
  List,
  ListItem,
  ListItemIcon,
  Switch,
  CircularProgress,
  Box,
  AppBar,
  Toolbar,
  FormControlLabel,
} from "@material-ui/core";
import { CheckCircle, Close, Search, PersonAdd } from "@material-ui/icons";
import { connect } from "react-redux";

import GameActions from "actions/games";
import PaymentAction from "../../actions/payments";
import paymentApi from "../../api/payment";
import { hideAuxiliaryDialog } from "../SharedComponents/AuxiliaryDialog";
import { hideDialog } from "../SharedComponents/Dialog";
import { switchComponent } from "../../actions/componentRouter";
import { Views } from "../Dashboard";
import { PayStatus, Player } from "../../types/player";
import { GlobalState } from "../../types/globalState";
import { User } from "../../types/user";
import { PaymentRuleFields } from "types/game";
import Config, { isOrganiserStripeReadyForPaidGames } from "config";
import { showSnackbar } from "components/SharedComponents/Notifier";
import { showAlertDialog } from "components/SharedComponents/AlertDialog";
import { userCanManageGame, userIsGameOwner } from "../../utils/gameOrganiser";
import { isRangeGame } from "../../utils/rhiGame";
import { calculateEntryFeeTotal } from "../../utils/paymentCollectionMethod";
import PaymentStatusBadge from "../SharedComponents/PaymentStatusBadge";
import { searchPlayers as searchPlayersByUserNameAction, addPlayerToFav, removePlayerFromFav } from "../../actions/players";
import { sortPlayersBySearchPriority } from "../../utils/playerFavourites";
import { PlayerFavouriteToggle } from "../SharedComponents/PlayerFavouriteToggle";
import { GameCredit } from "../../types/gameCredit";

const PLAYER_SEARCH_MIN_CHARS = 3;

const creditRemainingPence = (credit: GameCredit) =>
  typeof credit.remainingPence === "number" ? credit.remainingPence : credit.amountPence;

const creditPlayerId = (credit: GameCredit | any): string => {
  const player = credit?.player;
  if (!player) return "";
  if (typeof player === "string") return player;
  if (typeof player === "object") return String(player._id || player.id || "");
  return String(player);
};

const money = (pence: number) => `£${(pence / 100).toFixed(2)}`;

const autoStackCreditIds = (credits: GameCredit[], feePence: number): string[] => {
  if (!feePence || !credits.length) return [];
  const sorted = [...credits].sort((a, b) => {
    const ae = new Date(a.expiresAt).getTime();
    const be = new Date(b.expiresAt).getTime();
    return ae - be;
  });
  const ids: string[] = [];
  let need = feePence;
  for (const credit of sorted) {
    if (need <= 0) break;
    const bal = creditRemainingPence(credit);
    if (bal <= 0) continue;
    ids.push(credit._id);
    need -= bal;
  }
  return ids;
};

const stackCreditSpendPence = (credits: GameCredit[], feePence: number) => {
  let need = feePence;
  let spent = 0;
  for (const credit of credits) {
    if (need <= 0) break;
    const bal = creditRemainingPence(credit);
    if (bal <= 0) continue;
    const spend = Math.min(bal, need);
    spent += spend;
    need -= spend;
  }
  return spent;
};

const stackCoversFee = (credits: GameCredit[], feePence: number) => {
  return stackCreditSpendPence(credits, feePence) >= feePence;
};

interface Props extends WithStyles, Partial<WithWidth> {
  updateGame?(id: string, data: any): void;
  createStripeCheckoutSession?(data: { game: string | { _id: string }; players: string[]; payBeforeJoin?: boolean }, callback?: any): void;
  addWithoutPayment?(
    gameId: string,
    playersId: string[],
    payStatus: PayStatus,
    callback?: any,
    opts?: { creditIds?: string[] },
  ): void;
  addPlayer?(gameId: string, playerId: string, itsMe: boolean, callback?: (err: any) => void): void;
  getGameById?(
    id: string,
    queryType?: string,
    updateRedux?: boolean,
    callback?: (err: any, data: Game) => void,
  ): void;
  switchComponent?(view: Views, props?: any): void;
  searchPlayersByUserName?(query: string, callback: (result: any, err: any) => void): void;
  addPlayerToFav?(playerId: string, callback?: (err: any) => void): void;
  removePlayerFromFav?(playerId: string, callback?: (err: any) => void): void;
  game: Game;
  user?: User;
  type: "guest" | "pay" | "paid";
  payBeforeJoin?: boolean;
  onPaymentComplete?: () => void;
}

interface State {
  [index: string]: any;
  selectedPlayers: Map<string, boolean>;
  searchOpen: boolean;
  searchRes: any;
  searchTxt: string;
  checked: boolean;
  loading: boolean;
  stripeConnectReady: boolean | null;
  refreshedGame: Game | null;
  addingPlayerId: string | null;
  /** Player checkout: self plus anyone added via search (checkbox list, separate from selection). */
  paymentBasket: Map<string, Player>;
  /** Legacy: players mid-add before refresh; roster is source of truth after join API. */
  useGameCredit: boolean;
  openCredits: GameCredit[];
  selectedCreditByPlayer: Record<string, string[]>;
  externalPlayers: Map<string, Player>;
}

class GamePayment extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const { user, game, type } = props;
    const selectedPlayers = new Map<string, boolean>();
    const paymentBasket = new Map<string, Player>();
    if (user && type === "pay" && !userCanManageGame(game, user._id)) {
      const me = game.players?.[user._id];
      const selfUnpaid = !me || Number(me.payStatus) === PayStatus.NOT_PAID;
      if (selfUnpaid) {
        const selfPlayer = (me || {
          _id: user._id,
          name: user.name,
          lastName: user.lastName,
          username: user.username,
          payStatus: PayStatus.NOT_PAID,
        }) as Player;
        selectedPlayers.set(user._id, true);
        paymentBasket.set(user._id, selfPlayer);
      }
    }

    this.state = {
      selectedPlayers,
      searchOpen: false,
      loading: false,
      searchTxt: "",
      searchRes: [],
      checked: props.game.paymentRules.payBeforePlay,
      stripeConnectReady: null,
      refreshedGame: null,
      addingPlayerId: null,
      paymentBasket,
      externalPlayers: new Map(),
      useGameCredit: false,
      openCredits: [],
      selectedCreditByPlayer: {},
    };
  }

  componentDidMount() {
    const needsStripe = this.props.type === "pay" || this.props.payBeforeJoin;
    if (needsStripe) {
      this.loadStripeConnectStatus();
    }
    if (!this.isGuestMode()) this.loadOpenCredits();
    this.ensureDefaultSelfSelection();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.game._id !== this.props.game._id || prevProps.user?._id !== this.props.user?._id) {
      this.setState({ refreshedGame: null });
      this.pruneSelectedPlayers();
      this.ensureDefaultSelfSelection();
    } else if (prevProps.game !== this.props.game) {
      this.pruneSelectedPlayers();
    }
  }

  /** Player (non-organiser) payment uses the same roster picker as organiser, scoped to their group. */
  usesPlayerGroupPayFlow = () => this.props.type === "pay" && !this.isOrganiser();

  /** Paid games: search adds players pending checkout; roster join runs after successful payment. */
  canAddPlayersToGameInDialog = () => {
    const game = this.getGame();
    if (this.props.type !== "pay" || this.isGuestMode()) return false;
    return !!game.isPaid;
  };

  isPayCheckoutAllSettled = () =>
    this.isOrganiser() ? this.isOrganiserAllSettled() : this.isPlayerGroupAllSettled();

  userBriefToPlayer = (u: {
    _id: string;
    name: string;
    lastName?: string;
    username?: string;
    email?: string;
  }): Player =>
    ({
      _id: u._id,
      name: u.name,
      lastName: u.lastName,
      username: u.username,
      email: u.email,
      payStatus: PayStatus.NOT_PAID,
    }) as Player;

  getSelfPayPlayer = (): Player | null => {
    const { user } = this.props;
    if (!user) return null;
    const game = this.getGame();
    const rosterPlayer = game.players?.[user._id];
    if (rosterPlayer) return rosterPlayer;
    return this.userBriefToPlayer(user);
  };

  getSelectedPlayerList = (): Player[] => {
    const game = this.getGame();
    const { selectedPlayers, externalPlayers } = this.state;
    const list: Player[] = [];
    for (const id of selectedPlayers.keys()) {
      if (game.players?.[id]) {
        list.push(game.players[id]);
      } else if (externalPlayers.has(id)) {
        list.push(externalPlayers.get(id)!);
      } else if (id === this.props.user?._id) {
        const self = this.getSelfPayPlayer();
        if (self) list.push(self);
      }
    }
    return list;
  };

  getGame = () => this.state.refreshedGame || this.props.game;

  refreshGame = (callback?: () => void) => {
    const game = this.getGame();
    if (!this.props.getGameById) {
      callback?.();
      return;
    }
    this.props.getGameById(game._id, undefined, true, (err, data) => {
      if (!err && data) {
        this.setState({ refreshedGame: data }, callback);
      } else {
        callback?.();
      }
    });
  };

  addPlayerToSelection = (player: Player) => {
    const game = this.getGame();
    if (Number(player.payStatus) === PayStatus.PAID) return;
    if (game.players?.[player._id] && Number(game.players[player._id].payStatus) === PayStatus.PAID) return;
    if (this.state.addingPlayerId) return;

    const finishSelection = (playerRow: Player, pendingJoin = false) => {
      const selectedPlayers = new Map(this.state.selectedPlayers);
      selectedPlayers.set(player._id, true);

      const paymentBasket = new Map(this.state.paymentBasket);
      paymentBasket.set(player._id, playerRow);

      const externalPlayers = new Map(this.state.externalPlayers);
      if (pendingJoin) {
        externalPlayers.set(player._id, playerRow);
      } else {
        externalPlayers.delete(player._id);
      }

      this.setState({
        selectedPlayers,
        paymentBasket,
        externalPlayers,
        searchOpen: false,
        searchTxt: "",
        searchRes: [],
      });
    };

    if (game.players?.[player._id]) {
      finishSelection(game.players[player._id]);
      return;
    }

    if (this.canAddPlayersToGameInDialog()) {
      finishSelection(player, true);
      return;
    }

    if (!this.props.addPlayer) {
      showSnackbar("Could not add player to the game.", 4000, "error");
      return;
    }

    this.setState({ addingPlayerId: player._id });
    this.props.addPlayer(game._id, player._id, player._id === this.props.user?._id, err => {
      if (err) {
        this.setState({ addingPlayerId: null });
        if (err?.response?.data?.payload?.isFull) {
          showAlertDialog("Ooops!", "The game is full.", null);
        } else {
          const apiError = err?.response?.data?.error || err?.response?.data;
          showSnackbar(
            typeof apiError === "string" ? apiError : "Could not add player to the game.",
            6000,
            "error",
          );
        }
        return;
      }

      this.refreshGame(() => {
        const gameAfter = this.getGame();
        const rosterPlayer = gameAfter.players?.[player._id] || player;
        this.setState({ addingPlayerId: null }, () => finishSelection(rosterPlayer));
        showSnackbar(`${`${player.name} ${player.lastName || ""}`.trim()} has been added to the game.`);
      });
    });
  };

  getMyRosterPlayer = (): Player | undefined => {
    const { user } = this.props;
    if (!user) return undefined;
    return this.getGame().players?.[user._id];
  };

  getMyGroupName = (): string | undefined => this.getMyRosterPlayer()?.group?.name;

  isSamePayGroup = (player: Player) => {
    const myGroup = this.getMyGroupName();
    if (!myGroup) return true;
    return player.group?.name === myGroup;
  };

  getPlayerPayScopePlayers = (): Player[] => {
    const game = this.getGame();
    const { user } = this.props;
    if (!user) return [];
    const me = game.players?.[user._id];
    const roster = Object.values(game.players || {});

    if (this.props.payBeforeJoin && !me) {
      const rosterUnpaid = roster.filter(p => Number(p.payStatus) === PayStatus.NOT_PAID);
      const selfRow: Player = {
        _id: user._id,
        name: user.name,
        lastName: user.lastName,
        username: user.username,
        payStatus: PayStatus.NOT_PAID,
      } as Player;
      if (!rosterUnpaid.some(p => p._id === user._id)) {
        return [selfRow, ...rosterUnpaid];
      }
      return rosterUnpaid;
    }

    if (!me) return [];

    return roster.filter(p => {
      if (!this.isSamePayGroup(p)) return false;
      const status = Number(p.payStatus);
      return status === PayStatus.NOT_PAID || status === PayStatus.PAID;
    });
  };

  getPlayerPayScopeUnpaid = (): Player[] =>
    this.getPlayerPayScopePlayers().filter(p => Number(p.payStatus) === PayStatus.NOT_PAID);

  isPlayerGroupAllSettled = () => {
    if (!this.usesPlayerGroupPayFlow() || this.isGuestMode()) return false;

    if (this.canAddPlayersToGameInDialog()) {
      // Keep search/checkout available so players can add and pay for others after settling self.
      return false;
    }
    return this.getUnpaidPlayers().length === 0;
  };

  pruneSelectedPlayers = () => {
    const game = this.getGame();
    const next = new Map(this.state.selectedPlayers);
    const external = new Map(this.state.externalPlayers);
    const paymentBasket = new Map(this.state.paymentBasket);
    let changed = false;
    for (const id of next.keys()) {
      const rosterPlayer = game.players?.[id];
      const isSelf = id === this.props.user?._id;
      if (isSelf && !rosterPlayer && this.canAddPlayersToGameInDialog()) continue;
      if (rosterPlayer && Number(rosterPlayer.payStatus) !== PayStatus.NOT_PAID) {
        next.delete(id);
        changed = true;
      }
      if (!rosterPlayer && !external.has(id) && !(isSelf && this.canAddPlayersToGameInDialog())) {
        next.delete(id);
        changed = true;
      }
    }
    for (const id of paymentBasket.keys()) {
      const rosterPlayer = game.players?.[id];
      if (rosterPlayer && Number(rosterPlayer.payStatus) !== PayStatus.NOT_PAID) {
        paymentBasket.delete(id);
        changed = true;
      }
      const keepOffRosterSelf = id === this.props.user?._id && this.canAddPlayersToGameInDialog();
      if (!rosterPlayer && !next.has(id) && this.canAddPlayersToGameInDialog() && !keepOffRosterSelf) {
        paymentBasket.delete(id);
        changed = true;
      }
    }
    for (const id of external.keys()) {
      if (!next.has(id) && !game.players?.[id]) {
        external.delete(id);
        changed = true;
      }
    }
    if (changed) this.setState({ selectedPlayers: next, externalPlayers: external, paymentBasket });
  };

  ensureDefaultSelfSelection = () => {
    const { user } = this.props;
    if (!user || this.isOrganiser()) return;

    const self = this.getSelfPayPlayer();
    if (!self || Number(self.payStatus) === PayStatus.PAID) return;

    const paymentBasket = new Map(this.state.paymentBasket);
    const selectedPlayers = new Map(this.state.selectedPlayers);
    let changed = false;

    if (!paymentBasket.has(user._id)) {
      paymentBasket.set(user._id, self);
      changed = true;
    }
    if (!selectedPlayers.has(user._id)) {
      selectedPlayers.set(user._id, true);
      changed = true;
    }

    if (changed) this.setState({ paymentBasket, selectedPlayers });
  };

  loadStripeConnectStatus = () => {
    if (Config.DEV_BYPASS_STRIPE_CONNECT_READINESS) {
      this.setState({ stripeConnectReady: true });
      return;
    }
    const game = this.getGame();

    paymentApi
      .getStripeConnectStatusForGame(game._id)
      .then((res: { data: { ready?: boolean } }) => {
        this.setState({ stripeConnectReady: !!res.data.ready });
      })
      .catch(() => {
        const fallbackUser =
          typeof game.owner === "object" ? game.owner : undefined;
        this.setState({
          stripeConnectReady: isOrganiserStripeReadyForPaidGames(fallbackUser),
        });
      });
  };

  loadOpenCredits = () => {
    const userId = this.props.user?._id;
    const playerIds = this.isOrganiser() || !userId ? undefined : [userId];
    paymentApi
      .getOpenGameCredits(this.props.game._id, playerIds)
      .then((res: any) => {
        const body = res?.data;
        const data = Array.isArray(body)
          ? body
          : Array.isArray(body?.data)
            ? body.data
            : Array.isArray(body?.payload)
              ? body.payload
              : [];
        const openCredits = data.map((credit: any) => ({
          ...credit,
          player: creditPlayerId(credit),
          remainingPence: creditRemainingPence(credit),
        }));
        this.setState(prev => ({
          openCredits,
          selectedCreditByPlayer: this.syncCreditSelection(
            prev.selectedPlayers,
            openCredits,
            prev.selectedCreditByPlayer,
          ),
        }));
      })
      .catch(() => this.setState({ openCredits: [], selectedCreditByPlayer: {} }));
  };

  gameFeePence = () => {
    const fee = Number(this.props.game.paymentRules?.fee);
    if (!Number.isFinite(fee) || fee <= 0) return null;
    return Math.round(fee * 100);
  };

  creditsForPlayer = (playerId: string, openCredits: GameCredit[] = this.state.openCredits) => {
    const pid = String(playerId);
    return openCredits
      .filter(credit => creditPlayerId(credit) === pid && creditRemainingPence(credit) > 0)
      .sort((a, b) => new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime());
  };

  syncCreditSelection = (
    selectedPlayers: Map<string, boolean>,
    openCredits: GameCredit[],
    previous: Record<string, string[]>,
  ) => {
    const feePence = this.gameFeePence();
    const next: Record<string, string[]> = {};
    selectedPlayers.forEach((_, playerId) => {
      const options = this.creditsForPlayer(playerId, openCredits);
      if (!options.length || feePence === null) return;
      const prevIds = (previous[playerId] || []).filter(id => options.some(c => c._id === id));
      next[playerId] = prevIds.length ? prevIds : autoStackCreditIds(options, feePence);
    });
    return next;
  };

  selectedCreditsForPlayer = (playerId: string): GameCredit[] => {
    const ids = this.state.selectedCreditByPlayer[playerId] || [];
    const byId = new Map(this.state.openCredits.map(c => [c._id, c]));
    return ids.map(id => byId.get(id)).filter(Boolean) as GameCredit[];
  };

  creditsForSelection = () => {
    const selected: GameCredit[] = [];
    this.state.selectedPlayers.forEach((_, playerId) => {
      selected.push(...this.selectedCreditsForPlayer(playerId));
    });
    return selected;
  };

  canApplyCreditsToSelection = () => {
    if (this.state.selectedPlayers.size === 0) return false;
    let ok = true;
    this.state.selectedPlayers.forEach((_, playerId) => {
      if (!this.selectedCreditsForPlayer(playerId).length) ok = false;
    });
    return ok;
  };

  playerSelectionCovered = () => {
    if (!this.canApplyCreditsToSelection()) return false;
    let covered = true;
    this.state.selectedPlayers.forEach((_, playerId) => {
      if (!this.playerFeeCovered(playerId)) covered = false;
    });
    return covered;
  };

  playerCreditSpendPence = (playerId: string) => {
    const feePence = this.gameFeePence();
    if (feePence === null) return 0;
    return stackCreditSpendPence(this.selectedCreditsForPlayer(playerId), feePence);
  };

  playerCashRemainderPence = (playerId: string) => {
    const feePence = this.gameFeePence();
    if (feePence === null) return 0;
    return Math.max(0, feePence - this.playerCreditSpendPence(playerId));
  };

  playerCreditCoverSummary = (playerId: string) => {
    const feePence = this.gameFeePence();
    const selected = this.selectedCreditsForPlayer(playerId);
    if (feePence === null) return "";
    if (!selected.length) return `Select at least one credit toward ${money(feePence)}`;
    const spend = stackCreditSpendPence(selected, feePence);
    const cash = feePence - spend;
    const selectedTotal = selected.reduce((sum, c) => sum + creditRemainingPence(c), 0);
    const leftover = selectedTotal - spend;
    if (cash > 0) {
      return leftover > 0
        ? `${money(spend)} credit + ${money(cash)} by other means (${money(leftover)} stays on credit)`
        : `${money(spend)} credit + ${money(cash)} by other means`;
    }
    return leftover > 0
      ? `${money(spend)} covers this game (${money(leftover)} stays on credit)`
      : `${money(spend)} covers this game`;
  };

  selectionHasOpenCredits = () => {
    if (this.state.selectedPlayers.size !== 1) return false;
    let found = false;
    this.state.selectedPlayers.forEach((_, playerId) => {
      if (this.creditsForPlayer(playerId).length) found = true;
    });
    return found;
  };

  canOfferGameCredit = () => {
    if (this.isGuestMode() || isRangeGame(this.getGame()) || this.state.selectedPlayers.size !== 1) return false;
    if (!this.selectionHasOpenCredits()) return false;
    if (this.isOrganiser()) return true;
    const userId = this.props.user?._id;
    return Boolean(userId && this.state.selectedPlayers.has(userId));
  };

  onPayWithCreditChange = (checked: boolean) => {
    if (!checked || this.state.selectedPlayers.size !== 1) {
      this.setState({ useGameCredit: false });
      return;
    }
    const feePence = this.gameFeePence();
    const selectedCreditByPlayer: Record<string, string[]> = {};
    this.state.selectedPlayers.forEach((_, playerId) => {
      const options = this.creditsForPlayer(playerId);
      selectedCreditByPlayer[playerId] = feePence !== null ? autoStackCreditIds(options, feePence) : [];
    });
    this.setState({ useGameCredit: true, selectedCreditByPlayer });
  };

  onCreditToggle = (playerId: string, creditId: string) => {
    this.setState(prev => {
      const current = new Set(prev.selectedCreditByPlayer[playerId] || []);
      if (current.has(creditId)) current.delete(creditId);
      else current.add(creditId);
      return {
        selectedCreditByPlayer: {
          ...prev.selectedCreditByPlayer,
          [playerId]: [...current],
        },
      };
    });
  };

  playerFeeCovered = (playerId: string) => {
    const feePence = this.gameFeePence();
    if (feePence === null) return false;
    return stackCoversFee(this.selectedCreditsForPlayer(playerId), feePence);
  };

  isPlayerSelected = (playerId: string) => this.state.selectedPlayers?.has(playerId);

  onPlayerCheckboxChanged = (playerId: string) => {
    const game = this.getGame();
    const selectedPlayers = new Map(this.state.selectedPlayers);
    const externalPlayers = new Map(this.state.externalPlayers);
    const paymentBasket = new Map(this.state.paymentBasket);

    if (this.isPlayerSelected(playerId)) {
      if (!this.isOrganiser() && selectedPlayers.size <= 1) {
        showSnackbar("Select at least one player to pay for.", 4000, "warning");
        return;
      }
      selectedPlayers.delete(playerId);
      const isSelf = playerId === this.props.user?._id;
      // Keep unpaid off-roster self on the list after untick so they can tick again (PBP join).
      if (this.canAddPlayersToGameInDialog() && !game.players?.[playerId] && !isSelf) {
        externalPlayers.delete(playerId);
        paymentBasket.delete(playerId);
      }
    } else {
      selectedPlayers.set(playerId, true);
    }
    this.setState({
      selectedPlayers,
      externalPlayers,
      paymentBasket,
      useGameCredit: false,
      selectedCreditByPlayer: this.syncCreditSelection(
        selectedPlayers,
        this.state.openCredits,
        this.state.selectedCreditByPlayer,
      ),
    });
  };

  getPlayerPaymentBasketList = (): Player[] => {
    const game = this.getGame();
    const list: Player[] = [];
    for (const id of this.state.paymentBasket.keys()) {
      if (game.players?.[id]) {
        list.push(game.players[id]);
      } else {
        const cached = this.state.paymentBasket.get(id);
        if (cached) list.push(cached);
      }
    }
    return list;
  };

  getPayCheckoutDisplayPlayers = (): Player[] => {
    if (this.isGuestMode()) {
      return this.sortSearchResults(this.getPlayerPaymentBasketList());
    }
    const game = this.getGame();
    const byId = new Map<string, Player>();
    for (const player of this.getUnpaidPlayers()) {
      byId.set(player._id, player);
    }
    if (this.canAddPlayersToGameInDialog()) {
      for (const [id, player] of this.state.externalPlayers) {
        if (!game.players?.[id] && !byId.has(id)) {
          byId.set(id, player);
        }
      }
      // Off-roster selected payees live in the basket, not on the roster unpaid list.
      for (const [id, player] of this.state.paymentBasket) {
        if (!game.players?.[id] && !byId.has(id) && Number(player.payStatus) !== PayStatus.PAID) {
          byId.set(id, player);
        }
      }
    }
    const userId = this.props.user?._id;
    // Unpaid off-roster self stays on the list even after untick (PBP Join). Non-PBP
    // already has them via getUnpaidPlayers once they are on the roster.
    if (userId && this.props.type === "pay" && !this.isOrganiser() && !byId.has(userId)) {
      const self = this.getSelfPayPlayer();
      if (self && Number(self.payStatus) !== PayStatus.PAID) {
        byId.set(userId, self);
      }
    }
    return this.sortSearchResults([...byId.values()]);
  };

  calculateTotal = () => {
    const game = this.getGame();
    const playersCount = [...this.state.selectedPlayers.keys()].filter(id => {
      const rosterPlayer = game.players?.[id];
      return !rosterPlayer || Number(rosterPlayer.payStatus) !== PayStatus.PAID;
    }).length;

    return calculateEntryFeeTotal(game.paymentRules, playersCount).toFixed(2);
  };

  isGuestMode = () => this.props.type === "guest";

  isOrganiser = () => userCanManageGame(this.getGame(), this.props.user?._id);

  isGameOwner = () => userIsGameOwner(this.getGame(), this.props.user?._id);

  openStripeSetupInProfile = () => {
    hideAuxiliaryDialog();
    hideDialog();
    this.props.switchComponent?.(Views.Profile);
  };

  renderOrganiserStripeSetupNotice = () => {
    const { classes } = this.props;
    if (this.isGameOwner()) {
      return (
        <Box className={classes.stripeSetupNotice}>
          <Typography variant="caption" color="textSecondary" display="block" className={classes.submitNote}>
            Card payments need Stripe Connect before you can charge players. Open{" "}
            <strong>Profile → Payment</strong>, link your Stripe account, and complete onboarding until
            charges are enabled. You can still mark players paid off-app below.
          </Typography>
          <Button
            size="small"
            color="primary"
            className={classes.stripeSetupLink}
            onClick={this.openStripeSetupInProfile}
          >
            Set up Stripe in profile
          </Button>
        </Box>
      );
    }
    return (
      <Typography variant="caption" color="textSecondary" display="block" className={classes.submitNote}>
        Card payments are unavailable until the game owner completes Stripe setup in their profile.
        You can still mark players paid off-app below.
      </Typography>
    );
  };

  /** @deprecated Use usesPlayerGroupPayFlow — players now use the shared roster picker. */
  isSelfPayOnly = () => false;

  calculateSinglePlayerFee = () => this.getGame().paymentRules.fee;

  renderPlayerPaidBanner = () => {
    const { classes, user } = this.props;
    if (!user || !this.usesPlayerGroupPayFlow()) return null;

    const me = this.getGame().players?.[user._id];
    const payStatus = me ? Number(me.payStatus) : PayStatus.NOT_PAID;
    if (payStatus !== PayStatus.PAID) return null;

    return (
      <Box className={classes.statusNotice}>
        <CheckCircle className={classes.statusNoticeIcon} />
        <Box>
          <Typography variant="body2" className={classes.statusNoticeTitle}>
            Your entry fee is confirmed
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.statusNoticeBody}>
            Add other players below to pay for them in one checkout.
          </Typography>
        </Box>
      </Box>
    );
  };

  renderPlayerAllSettled = () => {
    const { classes } = this.props;
    return (
      <Box className={classes.statusNotice}>
        <CheckCircle className={classes.statusNoticeIcon} />
        <Box>
          <Typography variant="body2" className={classes.statusNoticeTitle}>
            All settled in your group
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.statusNoticeBody}>
            Everyone in your group is paid or marked as a guest. You can close this screen.
          </Typography>
        </Box>
      </Box>
    );
  };

  payWithStripe = (game: Game) => {
    if (this.state.loading) return;
    if (!this.state.stripeConnectReady) {
      if (this.isGameOwner()) {
        showSnackbar(
          "Complete Stripe setup in Profile → Payment before taking card payments.",
          6000,
          "error",
        );
      }
      return;
    }
    const userId = this.props.user?._id;
    const currentGame = this.getGame();
    let playerIds = [...this.state.selectedPlayers.keys()].filter(id => {
      const rosterPlayer = currentGame.players?.[id];
      return !rosterPlayer || Number(rosterPlayer.payStatus) !== PayStatus.PAID;
    });

    if (playerIds.length === 0) {
      showSnackbar("Select at least one unpaid player to pay for.", 4000, "warning");
      return;
    }

    const hasOffRosterCheckout = playerIds.some(id => !currentGame.players?.[id]);
    const payBeforeJoin = !!(this.props.payBeforeJoin || (currentGame.isPaid && hasOffRosterCheckout));
    if (payBeforeJoin && userId && !this.isOrganiser()) {
      const me = currentGame.players?.[userId];
      const selfNeedsPayment = !me || Number(me.payStatus) !== PayStatus.PAID;
      if (selfNeedsPayment && !playerIds.includes(userId)) {
        showSnackbar("Tick your own name to join and pay for yourself first.", 5000, "warning");
        return;
      }
    }

    this.setState({ loading: true });
    const data = {
      game: game._id,
      players: playerIds,
      payBeforeJoin,
    };
    this.props.createStripeCheckoutSession?.(data, (err: any, res: { url?: string }) => {
      this.setState({ loading: false });
      if (!err && res?.url) {
        window.location.href = res.url;
        return;
      }
      if (!err) {
        showSnackbar("Payment could not be started: no checkout URL was returned.", 6000, "error");
        return;
      }
      const responseData = err?.response?.data;
      const apiError =
        typeof responseData === "string"
          ? responseData
          : responseData?.error || responseData?.message;
      showSnackbar(
        apiError ||
          (err?.message === "Network Error"
            ? "Cannot reach the server. Check that the backend is running and try again."
            : err?.message) ||
          "Payment could not be started. Please try again.",
        6000,
        "error",
      );
    });
  };

  paidMarkConfirmCopy = (selectedCount: number, useCredit: boolean) => {
    const playerWord = selectedCount === 1 ? "player" : "players";
    if (useCredit && !this.isOrganiser()) {
      return {
        title: "Apply game credit?",
        message: "Use your game credit for this game. No card charge. You will be marked as paid.",
        confirm: "Apply game credit",
      };
    }
    if (useCredit && this.playerSelectionCovered()) {
      return {
        title: "Apply game credit?",
        message: `Mark ${selectedCount} ${playerWord} as paid using game credit. No card charge.`,
        confirm: "Apply game credit",
      };
    }
    if (useCredit) {
      return {
        title: "Mark as paid?",
        message: `Apply game credit for ${selectedCount} ${playerWord} and record the rest as cash or other means. No card charge.`,
        confirm: "Mark as paid",
      };
    }
    return {
      title: "Mark as paid (off-app)?",
      message: `Mark ${selectedCount} ${playerWord} as paid with no card charge. Only use this if they already paid you outside the app (cash, bank transfer, etc.).`,
      confirm: "Mark as paid",
    };
  };

  addWithoutPayment = (gameId: string) => {
    const payStatus = this.props.type === "guest" ? PayStatus.GUEST : PayStatus.PAID;
    const game = this.getGame();

    if (payStatus === PayStatus.PAID && isRangeGame(game)) {
      showSnackbar("Range games only accept in-app card payments.", 6000, "error");
      return;
    }

    if (!this.isOrganiser()) {
      const userId = this.props.user?._id;
      const selfOnly = userId && this.state.selectedPlayers.size === 1 && this.state.selectedPlayers.has(userId);
      if (!selfOnly || !this.state.useGameCredit) {
        showSnackbar("Only the game organiser can update payment status.", 6000, "error");
        return;
      }
    }
    if (this.state.selectedPlayers.size === 0) return;

    const selectedCount = this.state.selectedPlayers.size;
    const useCredit =
      payStatus === PayStatus.PAID && this.state.useGameCredit && this.state.selectedPlayers.size === 1;
    if (useCredit && !this.canApplyCreditsToSelection()) {
      showSnackbar("Select at least one game credit for each player, or turn off Pay with credit.", 7000, "error");
      return;
    }
    if (!this.isOrganiser() && useCredit && !this.playerSelectionCovered()) {
      showSnackbar(
        "Your credit does not cover the fee. Pay by card, or ask the organiser to apply it with other means.",
        7000,
        "error",
      );
      return;
    }
    const runMark = () => {
      this.setState({ loading: true });
      this.props.addWithoutPayment?.(
        gameId,
        [...this.state.selectedPlayers.keys()],
        payStatus,
        (err: any, data?: any) => {
          this.setState({ loading: false });
          if (!err) {
            this.setState({ selectedPlayers: new Map(), useGameCredit: false, selectedCreditByPlayer: {} });
            this.props.onPaymentComplete?.();
            const n = data?.redeemed?.length || 0;
            if (n) {
              showSnackbar(
                this.isOrganiser()
                  ? `Marked paid. Applied ${n} game credit voucher(s).`
                  : "Game credit applied. You're marked as paid.",
              );
            }
            else {
              showSnackbar(
                payStatus === PayStatus.GUEST ? "Marked as guest" : "Marked as paid",
                4000,
                "success",
              );
            }
            const failed = data?.failed?.length || 0;
            if (failed) {
              showSnackbar(
                `${failed} player credit stack(s) could not be applied. Payment details were refreshed.`,
                7000,
                "error",
              );
            }
            this.loadOpenCredits();
          } else {
            showSnackbar(err?.response?.data?.error || "Could not update payment status.", 6000, "error");
          }
        },
        useCredit ? { creditIds: this.creditsForSelection().map(credit => credit._id) } : undefined,
      );
    };

    if (payStatus === PayStatus.PAID) {
      const copy = this.paidMarkConfirmCopy(selectedCount, useCredit);
      showAlertDialog(copy.title, copy.message, runMark, () => {}, copy.confirm, "Cancel");
      return;
    }

    runMark();
  };

  openSearchOverlay = () => this.setState({ searchOpen: true });

  closeSearchOverlay = () => this.setState({ searchOpen: false, searchTxt: "", searchRes: [] });

  isMobileSearchLayout = () => isWidthDown("sm", this.props.width ?? "lg");

  onTextFieldchan = (field: string) => (event: any) => {
    const value = event.target.value as string;
    this.setState({ [field]: value });
    if (this.canAddPlayersToGameInDialog()) {
      this.searchGlobalPlayers(value.toLowerCase());
    } else {
      this.searchRosterPlayers(value.toLowerCase());
    }
  };

  searchRosterPlayers = (value: string) => {
    const baseList = this.usesPlayerGroupPayFlow()
      ? this.getPlayerPayScopeUnpaid()
      : Object.values(this.getGame().players || {}).filter(
          player => Number(player.payStatus) === PayStatus.NOT_PAID,
        );

    this.setState({
      searchRes: this.sortSearchResults(
        baseList.filter(
          player =>
            player.username?.toLowerCase().includes(value) ||
            player.name.toLowerCase().includes(value) ||
            player.lastName.toLowerCase().includes(value) ||
            player.email?.toLowerCase().includes(value),
        ),
      ),
    });
  };

  searchGlobalPlayers = (value: string) => {
    if (value.length < PLAYER_SEARCH_MIN_CHARS) {
      this.setState({ searchRes: [] });
      return;
    }
    this.props.searchPlayersByUserName?.(value, (result: any) => {
      const game = this.getGame();
      const rosterIds = new Set(Object.keys(game.players || {}));
      const pendingCheckoutIds = new Set([
        ...this.state.selectedPlayers.keys(),
        ...this.state.externalPlayers.keys(),
      ]);
      const query = value.toLowerCase();
      const users = (result?.data || []) as Array<{
        _id: string;
        name: string;
        lastName?: string;
        username?: string;
        email?: string;
      }>;
      const globalMatches = users
        .filter(u => !pendingCheckoutIds.has(u._id))
        .filter(u => !rosterIds.has(u._id) || Number(game.players[u._id]?.payStatus) === PayStatus.NOT_PAID)
        .map(u => this.userBriefToPlayer(u));

      const rosterMatches = Object.values(game.players || {})
        .filter(p => Number(p.payStatus) === PayStatus.NOT_PAID)
        .filter(p => !pendingCheckoutIds.has(p._id))
        .filter(
          p =>
            p.username?.toLowerCase().includes(query) ||
            p.name.toLowerCase().includes(query) ||
            (p.lastName || "").toLowerCase().includes(query),
        );

      const merged = [...rosterMatches];
      for (const g of globalMatches) {
        if (!merged.some(p => p._id === g._id)) merged.push(g);
      }
      this.setState({ searchRes: this.sortSearchResults(merged) });
    });
  };

  searchPlayers = (value: string) => {
    if (this.canAddPlayersToGameInDialog()) {
      this.searchGlobalPlayers(value);
    } else {
      this.searchRosterPlayers(value);
    }
  };

  clearSearch = () => {
    this.setState({ searchTxt: "", searchRes: [] });
  };

  getRosterPlayerIds = () => Object.keys(this.getGame().players || {});

  sortSearchResults = (players: Player[]) =>
    sortPlayersBySearchPriority(players, {
      favPlayerIds: this.props.user?.favPlayers,
      rosterPlayerIds: this.getRosterPlayerIds(),
    });

  togglePlayerFavourite = (playerId: string, favourited: boolean) => {
    if (favourited) {
      this.props.removePlayerFromFav?.(playerId);
    } else {
      this.props.addPlayerToFav?.(playerId);
    }
  };

  filterPlayersBySearchText = (players: Player[]) => {
    const query = this.state.searchTxt.trim().toLowerCase();
    if (!query) return players;
    return players.filter(
      p =>
        p.username?.toLowerCase().includes(query) ||
        p.name.toLowerCase().includes(query) ||
        (p.lastName || "").toLowerCase().includes(query) ||
        p.email?.toLowerCase().includes(query),
    );
  };

  renderSearchOverlay = () => {
    const { classes } = this.props;
    const { searchOpen, searchTxt, searchRes } = this.state;
    const addMode = this.canAddPlayersToGameInDialog();
    const isMobile = this.isMobileSearchLayout();

    return (
      <Dialog
        open={searchOpen}
        onClose={this.closeSearchOverlay}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        aria-labelledby="payment-player-search-title"
      >
        {isMobile ? (
          <AppBar position="static" color="default" elevation={0} className={classes.searchOverlayAppBar}>
            <Toolbar>
              <IconButton edge="start" onClick={this.closeSearchOverlay} aria-label="Close search">
                <Close />
              </IconButton>
              <Typography variant="subtitle1" className={classes.searchOverlayAppBarTitle}>
                Find players
              </Typography>
            </Toolbar>
          </AppBar>
        ) : (
          <DialogTitle disableTypography className={classes.searchOverlayDialogTitle}>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
              <Box>
                <Typography variant="h6" id="payment-player-search-title">
                  Find players
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Tap + to add someone to your checkout
                </Typography>
              </Box>
              <IconButton onClick={this.closeSearchOverlay} aria-label="Close search" size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
        )}
        <DialogContent className={classes.searchOverlayContent}>
          <InputBase
            value={searchTxt}
            placeholder={addMode ? "Search name or username" : "Username, name or email"}
            className={classes.searchField}
            inputProps={{ autoFocus: true, "aria-label": "Search players" }}
            onChange={this.onTextFieldchan("searchTxt")}
            startAdornment={
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            }
            endAdornment={
              searchTxt ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={this.clearSearch} aria-label="Clear search">
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          />
          {addMode && searchTxt.length > 0 && searchTxt.length < PLAYER_SEARCH_MIN_CHARS ? (
            <Typography variant="caption" color="textSecondary" className={classes.searchHint}>
              Type at least {PLAYER_SEARCH_MIN_CHARS} characters to search Schala players.
            </Typography>
          ) : null}
          {Array.isArray(searchRes) && searchRes.length > 0 ? (
            this.renderAddPlayerList(searchRes)
          ) : addMode && searchTxt.length >= PLAYER_SEARCH_MIN_CHARS ? (
            <Typography variant="body2" color="textSecondary" className={classes.searchHint}>
              No players found. Try another name or username.
            </Typography>
          ) : (
            <Typography variant="body2" color="textSecondary" className={classes.searchHint}>
              Search results appear here. Selected players show in your checkout below.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  renderPaymentSearchBar = () => {
    const { classes } = this.props;

    return (
      <Box className={classes.searchSection}>
        {this.renderSearchOverlay()}
        <InputBase
          fullWidth
          readOnly
          placeholder="Search name or username"
          className={classes.searchField}
          inputProps={{ "aria-label": "Search name or username", readOnly: true }}
          onClick={this.openSearchOverlay}
          onKeyDown={evt => {
            if (evt.key === "Enter" || evt.key === " ") {
              evt.preventDefault();
              this.openSearchOverlay();
            }
          }}
          startAdornment={
            <InputAdornment position="start">
              <Search color="action" />
            </InputAdornment>
          }
        />
      </Box>
    );
  };

  renderAddPlayerList = (players: Player[]) => {
    const { classes, user } = this.props;
    const { addingPlayerId } = this.state;
    const fee = this.calculateSinglePlayerFee().toFixed(2);
    return (
      <List className={classes.searchResultList} disablePadding>
        {players.map(player => {
          const isAdding = addingPlayerId === player._id;
          return (
            <ListItem
              button
              key={player._id}
              className={classes.playerRow}
              disabled={!!addingPlayerId}
              onClick={() => this.addPlayerToSelection(player)}
            >
              <Box className={classes.playerRowBody}>
                <Box className={classes.playerRowText}>
                  <Box className={classes.playerRowNameLine}>
                    <Typography variant="body1" className={classes.playerRowName} noWrap>
                      {`${player.name} ${player.lastName || ""}`.trim()}
                    </Typography>
                    <PlayerFavouriteToggle
                      playerId={player._id}
                      currentUserId={user?._id}
                      favPlayerIds={user?.favPlayers}
                      onToggle={this.togglePlayerFavourite}
                      className={classes.playerFavToggle}
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary" noWrap>
                    {player.username || ""}
                  </Typography>
                </Box>
                <Box className={classes.playerRowBadge}>
                  {isAdding ? (
                    <CircularProgress size={20} />
                  ) : (
                    <IconButton size="small" aria-label="Add player" color="primary">
                      <PersonAdd />
                    </IconButton>
                  )}
                  <Typography variant="body2" className={classes.playerRowFee}>
                    £{fee}
                  </Typography>
                </Box>
              </Box>
            </ListItem>
          );
        })}
      </List>
    );
  };

  getRenderList = (player: Player) => {
    const game = this.getGame();
    const { selectedPlayers } = this.state;
    if (this.usesPlayerGroupPayFlow() && this.props.type === "pay" && !this.isGuestMode()) {
      return this.getPlayerPayScopePlayers();
    }
    if (userCanManageGame(game, this.props.user?._id)) {
      if (this.props.type === "pay" && !this.isGuestMode()) {
        return Object.values(game.players || {}).filter(p => Number(p.payStatus) === PayStatus.NOT_PAID);
      }
      return Object.values(game.players || {});
    }
    return Object.values(game.players || {}).filter(
      x => x.group?.name === player?.group?.name || selectedPlayers.has(x._id),
    );
  };

  renderNameList = (players: Player[]) => {
    const { user, classes } = this.props;
    if (!user) return null;
    const fee = this.calculateSinglePlayerFee().toFixed(2);
    const showFee = !this.isGuestMode();
    return (
      <List className={classes.playerList}>
        {players.map(player => {
          const alreadyAdded = this.state.selectedPlayers.has(player._id);
          const payStatus = Number(player.payStatus);
          const isPaid = payStatus === PayStatus.PAID;
          const isGuest = payStatus === PayStatus.GUEST;
          const { type } = this.props;
          const rowLocked =
            isPaid || (type === "guest" && isGuest) || (type === "pay" && isGuest && !this.isOrganiser());
          return (
            <ListItem
              className={`${classes.playerRow}${user._id === player._id ? ` ${classes.currentUserRow}` : ""}`}
              button
              key={player._id}
              disabled={rowLocked}
              onClick={() => this.onPlayerCheckboxChanged(player._id)}
            >
              <ListItemIcon className={classes.playerRowCheckbox}>
                <Checkbox disabled={rowLocked} edge="start" checked={alreadyAdded} disableRipple />
              </ListItemIcon>
              <Box className={classes.playerRowBody}>
                <Box className={classes.playerRowText}>
                  <Box className={classes.playerRowNameLine}>
                    <Typography variant="body1" className={classes.playerRowName} noWrap>
                      {`${player.name} ${player.lastName || ""}`}
                    </Typography>
                    <PlayerFavouriteToggle
                      playerId={player._id}
                      currentUserId={user._id}
                      favPlayerIds={user.favPlayers}
                      onToggle={this.togglePlayerFavourite}
                      className={classes.playerFavToggle}
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary" noWrap>
                    {player.username}
                  </Typography>
                </Box>
                <Box className={classes.playerRowBadge}>
                  {showFee && !isPaid && !isGuest ? (
                    <Typography variant="body2" className={classes.playerRowFee}>
                      £{fee}
                    </Typography>
                  ) : null}
                  {!showFee || isPaid || isGuest ? (
                    <PaymentStatusBadge payStatus={payStatus} showPaid showLabels className={classes.playerRowPayBadge} />
                  ) : null}
                </Box>
              </Box>
            </ListItem>
          );
        })}
      </List>
    );
  };

  renderCreditPicker = () => {
    const { classes } = this.props;
    const game = this.getGame();
    if (!this.canOfferGameCredit()) return null;

    return (
      <Box className={classes.creditPanel}>
        <FormControlLabel
          className={classes.payWithCreditLabel}
          control={
            <Checkbox
              color="secondary"
              checked={this.state.useGameCredit}
              onChange={evt => this.onPayWithCreditChange(evt.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="body1" className={classes.payWithCreditTitle}>
                Pay with credit
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" className={classes.submitNote}>
                Use store credit from a prior leave toward this entry fee.
              </Typography>
            </Box>
          }
        />

        {this.state.useGameCredit ? (
          <Box className={classes.creditExpanded}>
            <Divider className={classes.actionDivider} />
            {[...this.state.selectedPlayers.keys()].map(playerId => {
              const options = this.creditsForPlayer(playerId);
              const player = game.players[playerId];
              const label = player
                ? `${player.name || ""} ${player.lastName || ""}`.trim() || player.username
                : "Player";

              if (!options.length) {
                return (
                  <Typography key={playerId} variant="body2" color="textSecondary" className={classes.sectionHint}>
                    {label} has no open credit for this fee.
                  </Typography>
                );
              }

              const selectedIds = new Set(this.state.selectedCreditByPlayer[playerId] || []);
              const hasSelection = selectedIds.size > 0;
              const covered = this.playerFeeCovered(playerId);
              return (
                <Box key={playerId} className={classes.creditBlock}>
                  <Typography variant="subtitle2" className={classes.creditPlayerLabel}>
                    Credits for {label}
                  </Typography>
                  <List dense disablePadding className={classes.creditList}>
                    {options.map(credit => {
                      const remaining = creditRemainingPence(credit);
                      const same = remaining === credit.amountPence;
                      const checked = selectedIds.has(credit._id);
                      const gameTitle = (credit.sourceGameName || "").trim() || "Prior game";
                      const gameCode = (credit.sourceGameCode || "").trim();
                      const metaParts = [
                        gameCode || null,
                        `Expires ${new Date(credit.expiresAt).toLocaleDateString()}`,
                        !same ? `${money(remaining)} of ${money(credit.amountPence)}` : null,
                      ].filter(Boolean);
                      return (
                        <ListItem
                          key={credit._id}
                          dense
                          button
                          className={classes.creditRow}
                          onClick={() => this.onCreditToggle(playerId, credit._id)}
                        >
                          <ListItemIcon className={classes.creditRowCheckbox}>
                            <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple color="secondary" />
                          </ListItemIcon>
                          <Box className={classes.playerRowBody}>
                            <Box className={classes.playerRowText}>
                              <Typography variant="body2" className={classes.creditRowPrimary} noWrap>
                                {gameTitle}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" display="block" noWrap>
                                {metaParts.join(" · ")}
                              </Typography>
                            </Box>
                            <Typography variant="body2" className={classes.playerRowFee}>
                              {money(remaining)}
                            </Typography>
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                  <Box
                    className={`${classes.coverNotice} ${
                      hasSelection ? classes.coverNoticeOk : classes.coverNoticeWarn
                    }`}
                  >
                    {hasSelection ? <CheckCircle className={classes.coverNoticeIcon} fontSize="small" /> : null}
                    <Box>
                      <Typography variant="caption" className={classes.coverNoticeText} display="block">
                        {this.playerCreditCoverSummary(playerId)}
                      </Typography>
                      {hasSelection && !covered ? (
                        <Typography variant="caption" color="textSecondary" display="block">
                          Mark as paid records the rest as cash / other means.
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : null}
      </Box>
    );
  };

  getUnpaidPlayers = () => {
    const game = this.getGame();
    return Object.values(game.players || {}).filter(p => Number(p.payStatus) === PayStatus.NOT_PAID);
  };

  isOrganiserAllSettled = () => {
    if (!this.isOrganiser() || this.props.type !== "pay" || this.isGuestMode()) return false;

    if (this.canAddPlayersToGameInDialog()) {
      // Keep search/checkout available so organisers can add players after the roster is fully paid.
      return false;
    }

    return this.getUnpaidPlayers().length === 0;
  };

  renderOrganiserAllSettled = () => {
    const { classes } = this.props;
    const players = Object.values(this.getGame().players || {});
    const paidCount = players.filter(p => Number(p.payStatus) === PayStatus.PAID).length;
    const guestCount = players.filter(p => Number(p.payStatus) === PayStatus.GUEST).length;
    const rosterParts = [
      paidCount > 0 ? `${paidCount} paid` : null,
      guestCount > 0 ? `${guestCount} guest` : null,
    ].filter(Boolean);

    return (
      <Box className={classes.statusNotice}>
        <CheckCircle className={classes.statusNoticeIcon} />
        <Box>
          <Typography variant="body2" className={classes.statusNoticeTitle}>
            All settled
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.statusNoticeBody}>
            Everyone on the roster is paid or marked as a guest. Nothing to collect here.
          </Typography>
          {rosterParts.length > 0 ? (
            <Typography variant="caption" color="textSecondary" className={classes.organiserRosterSummary}>
              {rosterParts.join(", ")}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  };

  shouldShowPayCheckoutFooter = () => {
    const count = this.state.selectedPlayers.size;
    if (count > 0) return true;
    if (this.isOrganiser()) return true;
    return this.getPayCheckoutDisplayPlayers().length > 0;
  };

  renderPayCheckoutFooter = (game: Game) => {
    const { classes } = this.props;
    const { loading, stripeConnectReady, selectedPlayers } = this.state;
    const count = selectedPlayers.size;
    const organiser = this.isOrganiser();
    const rangeGame = ENABLE_RHI && isRangeGame(game);
    const allowOffApp = organiser && !rangeGame;
    const playerCountLabel = count === 1 ? "1 player" : `${count} players`;

    if (count === 0) {
      return (
        <Typography variant="body2" color="textSecondary" className={classes.actionPrompt}>
          Select at least one player to pay.
        </Typography>
      );
    }

    return (
      <Box className={classes.footerStack}>
        <Box className={classes.totalRow}>
          <Typography variant="body2" color="textSecondary">
            Total to pay ({playerCountLabel})
          </Typography>
          <Typography variant="h6" className={classes.submitTotal}>
            £{this.calculateTotal()}
          </Typography>
        </Box>

        {ENABLE_STRIPE ? (
          <>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disableElevation={false}
              className={classes.primaryPayButton}
              onClick={() => this.payWithStripe(game)}
              disabled={loading || stripeConnectReady === false}
            >
              {loading ? <CircularProgress size={20} style={{ marginRight: 10, color: "#fff" }} /> : null}
              Pay £{this.calculateTotal()}
            </Button>
            {stripeConnectReady === false ? (
              organiser ? (
                this.renderOrganiserStripeSetupNotice()
              ) : (
                <Typography variant="caption" color="textSecondary" display="block" className={classes.submitNote}>
                  Payment is not available yet. The organiser has not finished payment setup. Please contact them.
                </Typography>
              )
            ) : (
          <Typography variant="caption" color="textSecondary" display="block" className={classes.submitNote}>
            Opens Stripe Checkout. Schala and Stripe fees apply.
          </Typography>
        )}
          </>
        ) : null}

        {!organiser && this.canOfferGameCredit() ? (
          <>
            <Divider className={classes.actionDivider} />
            {this.renderCreditPicker()}
            {this.state.useGameCredit ? (
              <>
                <Button
                  fullWidth
                  variant="outlined"
                  size="medium"
                  className={classes.secondaryPayButton}
                  onClick={() => this.addWithoutPayment(game._id)}
                  disabled={loading || !this.playerSelectionCovered()}
                >
                  Apply game credit
                </Button>
                <Typography variant="caption" color="textSecondary" display="block" className={classes.submitNote}>
                  {this.playerSelectionCovered()
                    ? "Uses your credit with this organiser. No card charge."
                    : "Credit does not cover the fee. Pay by card, or ask the organiser to apply it with other means."}
                </Typography>
              </>
            ) : null}
          </>
        ) : null}

        {allowOffApp ? (
          <>
            <Divider className={classes.actionDivider} />
            {this.renderCreditPicker()}

            <Button
              fullWidth
              variant="outlined"
              size="medium"
              className={classes.secondaryPayButton}
              onClick={() => this.addWithoutPayment(game._id)}
              disabled={loading || (this.state.useGameCredit && !this.canApplyCreditsToSelection())}
            >
              {this.state.useGameCredit
                ? this.playerSelectionCovered()
                  ? "Apply game credit"
                  : `Mark ${count} as paid`
                : `Mark ${count} as paid (off-app)`}
            </Button>
            <Typography variant="caption" color="textSecondary" display="block" className={classes.submitNote}>
              {this.state.useGameCredit
                ? "Applies selected credit and records any remainder as cash / other means. No card charge."
                : "Cash or bank transfer already received. No card charge."}
            </Typography>
          </>
        ) : organiser && rangeGame ? (
          <Typography variant="caption" color="textSecondary" display="block" className={classes.submitNote}>
            Range Handicap games only accept in-app card payments.
          </Typography>
        ) : null}
      </Box>
    );
  };

  renderPaymentActions = (game: Game) => {
    const { classes } = this.props;
    const { loading } = this.state;
    const organiser = this.isOrganiser();

    if (!this.isGuestMode()) {
      return this.renderPayCheckoutFooter(game);
    }

    if (this.isGuestMode()) {
      if (!organiser) {
        return (
          <Typography variant="caption" color="textSecondary">
            Only the game organiser can mark guests.
          </Typography>
        );
      }
      return (
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={() => this.addWithoutPayment(game._id)}
          disabled={this.state.selectedPlayers.size === 0 || loading}
        >
          {loading ? <CircularProgress size={18} style={{ marginRight: 8 }} /> : null}
          Set as guest
        </Button>
      );
    }

    return null;
  };

  render() {
    const { classes, user } = this.props;
    if (!user) return null;
    const game = this.getGame();
    const isOrganiser = this.isOrganiser();

    if (this.isGuestMode()) {
      const me = game.players[user._id];
      const players = this.filterPlayersBySearchText(this.getRenderList(me) as Player[]);

      return (
        <div className={classes.container}>
          {isOrganiser && (
            <div className={classes.inline}>
              <Typography variant="subtitle1">Pay Before Play</Typography>
              <Switch
                checked={this.state.checked}
                onChange={evt => {
                  this.props.updateGame?.(game._id, {
                    pot: game.pot,
                    isPaid: game.isPaid,
                    date: game.date,
                    onlyPayment: true,
                    paymentRules: { ...game.paymentRules, [PaymentRuleFields.PAY_BEFORE_PLAY]: evt.target.checked },
                  });
                  this.setState({ checked: evt.target.checked });
                }}
              />
            </div>
          )}

          <Typography variant="h6" className={classes.sectionTitle}>
            Select players
          </Typography>
          {players.length === 0 ? (
            <Typography variant="body2" color="textSecondary" className={classes.sectionHint}>
              No matching players. Try a different search.
            </Typography>
          ) : (
            this.renderNameList(players as Player[])
          )}

          <Box className={classes.footer}>{this.renderPaymentActions(game)}</Box>
        </div>
      );
    }

    const allSettled = this.isPayCheckoutAllSettled();
    const checkoutPlayers = this.getPayCheckoutDisplayPlayers();

    return (
      <div className={classes.container}>
        {isOrganiser && (
          <div className={classes.inline}>
            <Typography variant="subtitle1">Pay Before Play</Typography>
            <Switch
              checked={this.state.checked}
              onChange={evt => {
                this.props.updateGame?.(game._id, {
                  pot: game.pot,
                  isPaid: game.isPaid,
                  date: game.date,
                  onlyPayment: true,
                  paymentRules: { ...game.paymentRules, [PaymentRuleFields.PAY_BEFORE_PLAY]: evt.target.checked },
                });
                this.setState({ checked: evt.target.checked });
              }}
            />
          </div>
        )}

        {!isOrganiser && this.renderPlayerPaidBanner()}

        {allSettled ? (
          isOrganiser ? this.renderOrganiserAllSettled() : this.renderPlayerAllSettled()
        ) : (
          <>
            {this.renderPaymentSearchBar()}

            {checkoutPlayers.length > 0 ? (
              <>
                <Typography variant="subtitle2" className={classes.sectionTitle}>
                  Players to pay
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" className={classes.sectionHint}>
                  Tick who you are paying for. Use + in search to add someone.
                </Typography>
                {this.renderNameList(checkoutPlayers)}
              </>
            ) : (
              <Typography variant="body2" color="textSecondary" className={classes.sectionHint}>
                {isOrganiser
                  ? "No unpaid players on the roster. Search above to add someone."
                  : "Search above to add players to pay for."}
              </Typography>
            )}
          </>
        )}

        {!allSettled && this.shouldShowPayCheckoutFooter() ? (
          <Box className={classes.footer}>{this.renderPaymentActions(game)}</Box>
        ) : null}
      </div>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    container: {
      padding: "20px 30px",
      minWidth: 350,

      [theme.breakpoints.down("xs")]: {
        minWidth: "auto",
        padding: theme.spacing(2, 1.5),
      },
    },
    inline: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    button: {
      textAlign: "center",
      width: "fit-content",
      fontWeight: "bold",
      fontSize: "1rem",
      lineHeight: 1.6,
      [theme.breakpoints.down("xs")]: {
        width: "100%",
      },
    },
    sectionTitle: {
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(0.75),
      fontWeight: 600,
      fontSize: "0.8125rem",
      color: theme.palette.text.secondary,
    },
    sectionHint: {
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(1),
      lineHeight: 1.5,
    },
    statusNotice: {
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(1.25),
      marginBottom: theme.spacing(2),
    },
    statusNoticeIcon: {
      color: theme.palette.success.main,
      fontSize: 20,
      marginTop: 2,
      flexShrink: 0,
    },
    statusNoticeTitle: {
      fontWeight: 600,
      lineHeight: 1.4,
    },
    statusNoticeBody: {
      lineHeight: 1.5,
      marginTop: theme.spacing(0.25),
    },
    playerList: {
      width: "100%",
    },
    playerRow: {
      alignItems: "center",
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2.5),
      [theme.breakpoints.down("xs")]: {
        paddingLeft: theme.spacing(2.5),
        paddingRight: theme.spacing(3),
      },
    },
    playerRowCheckbox: {
      minWidth: 42,
      [theme.breakpoints.down("xs")]: {
        minWidth: 36,
        marginRight: theme.spacing(1),
      },
    },
    playerRowBody: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flex: "1 1 auto",
      minWidth: 0,
      width: "100%",
      gap: theme.spacing(1),
    },
    playerFavToggle: {
      flexShrink: 0,
    },
    playerRowText: {
      flex: "1 1 auto",
      minWidth: 0,
      overflow: "hidden",
    },
    playerRowName: {
      lineHeight: 1.25,
      minWidth: 0,
    },
    playerRowNameLine: {
      display: "flex",
      alignItems: "center",
      gap: 0,
      minWidth: 0,
    },
    playerRowBadge: {
      display: "flex",
      alignItems: "center",
      flexShrink: 0,
      marginLeft: "auto",
      gap: theme.spacing(0.5),
    },
    playerRowPayBadge: {
      marginLeft: 0,
    },
    playerRowFee: {
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
    },
    searchHint: {
      display: "block",
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    footer: {
      marginTop: theme.spacing(3),
      paddingTop: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.divider}`,
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      },
    },
    footerStack: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.25),
    },
    submitBlock: {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1.25),
    },
    totalRow: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: theme.spacing(2),
    },
    submitTotal: {
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      lineHeight: 1.2,
    },
    submitNote: {
      lineHeight: 1.5,
    },
    stripeSetupNotice: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(0.75),
      padding: theme.spacing(1.25, 1.5),
      borderRadius: 8,
      backgroundColor: "#fff8e6",
      border: `1px solid ${theme.palette.grey[300]}`,
    },
    stripeSetupLink: {
      alignSelf: "flex-start",
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.8125rem",
      padding: theme.spacing(0.25, 0),
      minWidth: 0,
    },
    actionPrompt: {
      lineHeight: 1.5,
    },
    actionDivider: {
      margin: theme.spacing(0.5, 0),
      backgroundColor: theme.palette.grey[300],
    },
    primaryPayButton: {
      fontWeight: 700,
      fontSize: "1rem",
      minHeight: 52,
      padding: theme.spacing(1.5, 2.5),
      borderRadius: 8,
      textTransform: "none" as const,
      boxShadow: theme.shadows[3],
      transition: "transform 0.15s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.15s ease",
      "&:not(:disabled):hover": {
        boxShadow: theme.shadows[6],
      },
      "&:not(:disabled):active": {
        transform: "scale(0.985)",
        boxShadow: theme.shadows[2],
      },
    },
    secondaryPayButton: {
      fontWeight: 600,
      fontSize: "0.9375rem",
      minHeight: 44,
      padding: theme.spacing(1.25, 2),
      borderRadius: 8,
      textTransform: "none" as const,
      borderWidth: 2,
      borderColor: theme.palette.grey[400],
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      transition: "transform 0.15s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.15s ease, background-color 0.15s ease",
      "&:not(:disabled):hover": {
        borderWidth: 2,
        borderColor: theme.palette.grey[600],
        backgroundColor: theme.palette.grey[50],
      },
      "&:not(:disabled):active": {
        transform: "scale(0.985)",
      },
    },
    cardButton: {
      fontWeight: 700,
      fontSize: "1rem",
      minHeight: 52,
      padding: theme.spacing(1.5, 2.5),
      borderRadius: 8,
      boxShadow: theme.shadows[3],
      textTransform: "none" as const,
      transition: "transform 0.15s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.15s ease",
      "&:not(:disabled):hover": {
        boxShadow: theme.shadows[6],
      },
      "&:not(:disabled):active": {
        transform: "scale(0.985)",
      },
    },
    organiserRosterSummary: {
      display: "block",
      marginTop: theme.spacing(0.75),
      lineHeight: 1.4,
    },
    currentUserRow: {
      backgroundColor: "#cce8cf",
    },
    creditPanel: {
      marginTop: theme.spacing(1.5),
      padding: theme.spacing(1.25, 1.5),
      borderRadius: 8,
      backgroundColor: theme.palette.grey[50],
      border: `1px solid ${theme.palette.grey[300]}`,
    },
    payWithCreditLabel: {
      marginLeft: 0,
      marginRight: 0,
      alignItems: "flex-start",
      width: "100%",
    },
    payWithCreditTitle: {
      fontWeight: 600,
      lineHeight: 1.3,
    },
    creditExpanded: {
      marginTop: theme.spacing(0.5),
    },
    creditBlock: {
      marginTop: theme.spacing(1),
    },
    creditPlayerLabel: {
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
      color: theme.palette.text.secondary,
      fontSize: "0.8125rem",
    },
    creditList: {
      marginLeft: theme.spacing(1),
      paddingLeft: theme.spacing(1),
    },
    creditRow: {
      borderRadius: 8,
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(1),
      marginBottom: 2,
    },
    creditRowCheckbox: {
      minWidth: 36,
    },
    creditRowPrimary: {
      fontWeight: 500,
      lineHeight: 1.3,
    },
    coverNotice: {
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(0.75),
      marginTop: theme.spacing(1),
      marginLeft: theme.spacing(1),
      padding: theme.spacing(1, 1.25),
      borderRadius: 8,
    },
    coverNoticeOk: {
      backgroundColor: "#E8F5E9",
      border: "1px solid #C8E6C9",
    },
    coverNoticeWarn: {
      backgroundColor: "#fff8e6",
      border: `1px solid ${theme.palette.grey[300]}`,
    },
    coverNoticeIcon: {
      color: theme.palette.success.main,
      marginTop: 1,
      flexShrink: 0,
    },
    coverNoticeText: {
      lineHeight: 1.45,
      color: theme.palette.text.secondary,
    },
    searchSection: {
      width: "100%",
    },
    searchField: {
      width: "100%",
      boxSizing: "border-box",
      minHeight: 44,
      padding: theme.spacing(1, 1.5),
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.grey[300]}`,
      backgroundColor: theme.palette.background.paper,
      fontSize: "1rem",
      cursor: "pointer",
      "& input::placeholder": {
        color: theme.palette.text.secondary,
        opacity: 1,
      },
    },
    searchOverlayAppBar: {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    searchOverlayAppBarTitle: {
      fontWeight: 600,
      marginLeft: theme.spacing(0.5),
    },
    searchOverlayDialogTitle: {
      paddingBottom: theme.spacing(1),
    },
    searchOverlayContent: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(3),
    },
    searchResultList: {
      marginTop: theme.spacing(1),
      overflow: "auto",
      [theme.breakpoints.up("sm")]: {
        maxHeight: 360,
      },
    },
  });

const mapStateToProps = (state: GlobalState) => {
  const { user } = state.auth;
  return {
    user,
  };
};

export default connect(mapStateToProps, {
  createStripeCheckoutSession: PaymentAction.createStripeCheckoutSession,
  addWithoutPayment: PaymentAction.addWithoutPayment,
  addPlayer: GameActions.addPlayer,
  getGameById: GameActions.getById,
  updateGame: GameActions.update,
  switchComponent,
  searchPlayersByUserName: searchPlayersByUserNameAction,
  addPlayerToFav,
  removePlayerFromFav,
})(withWidth()(withStyles(styles)(GamePayment)));
